import { useEffect, useRef, useState } from 'react';
import {
  BODY_TYPE_LABEL,
  deliveryEta,
  ENTITY_TYPE_LABEL,
  LOADING_TYPE_LABEL,
  medianOf,
  PAYMENT_TYPE_LABEL,
  RATE_TONE_HINT,
  rateTone,
  formatMoney,
  SIMILAR_TITLE,
  ACTION_LABEL,
  bidRejection,
  nextBid,
  CURRENCY_SIGN,
  OFFER_STATUS_LABEL,
} from 'shared';
import type { BodyType, DealBreakdown, FileRow, SimilarCargos } from 'shared';
import { FileGrid, UploadButton } from '../Files';
import Icon from '../Icon';
import RouteLine from '../Route';
import { Money } from '../money';
import {
  api,
  type CargoDetail as Detail,
  type CargoHistoryRow,
  type Offer,
  type Vehicle,
} from '../api';
import { alert, confirm, copyText, notify } from '../telegram';
import { toast } from '../toast';
import { cargoToText } from '../cargoText';
import { useRealtime, syncClock } from '../realtime';
import { useFeature } from '../features';
import { useSettings } from '../Support';

interface Props {
  id: string;
  onDone: () => void;
  /** Открыть заявку на правку. Доступно автору, пока её не взяли. */
  onEdit: (id: string) => void;
  /** Открыть другую заявку — из блока похожих. */
  onOpen: (id: string) => void;
}

const historyWhen = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Рубли учёта → валюта сделки, по курсу самой заявки.
 *
 * Наценка и «на руки» считаются в рублях: контракт задан в рублях, и
 * менять это ради показа нельзя. Но человеку надо видеть суммы в той же
 * валюте, в которой он назвал ставку, — иначе «ставка 450 000 ₸, на руки
 * 82 300 ₽» читается как ошибка.
 *
 * Курс берётся у заявки, а не сегодняшний: он тот же, по которому
 * посчитан рублёвый эквивалент ставки, и расхождения между строками
 * одного блока быть не должно.
 */
function makeToDeal(rate: number) {
  return (rub: number) => (rate > 0 ? Math.round(rub / rate) : rub);
}

/**
 * Что подставить в поле ставки.
 *
 * В торгах — наибольшую проходящую ставку: перевозчик почти всегда хочет
 * перебить на минимум, и заставлять его вычислять «лучшая минус шаг» в
 * уме, пока идёт отсчёт, — ровно та работа, которую должна делать
 * программа. В обычной заявке — цену заявки: согласиться на объявленное
 * условие тоже должно быть одним нажатием.
 *
 * Обе цифры — в валюте сделки, в ней же уходит отклик.
 */
function suggestBid(c: Detail): number {
  if (c.biddingMode !== 'AUCTION') return c.price;
  const mine = c.myOffer != null && c.bestBid === c.myOffer.price;
  return (
    nextBid({ startPrice: c.price, bestBid: c.bestBid, bestIsMine: mine, step: c.bidStep }) ??
    c.price
  );
}

/** Срок торгов человеческим видом: дата коротко, время точно. */
function bidDeadline(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Значение «было/стало» одной строкой. */
function showValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (v === true) return 'да';
  if (v === false) return 'нет';
  if (Array.isArray(v)) return v.join(', ') || '—';
  if (typeof v === 'number') return v.toLocaleString('ru-RU');
  return String(v);
}

/**
 * Почему машина не годится под заявку, или null, если годится.
 *
 * Первая же причина и возвращается: перечислять «и кузов, и тоннаж, и объём»
 * в строке списка бессмысленно — решение всё равно одно, машину не берут.
 */
function whyNotFit(v: Vehicle, cargo: Detail): string | null {
  if (cargo.bodyTypes.length > 0 && !cargo.bodyTypes.includes(v.bodyType as BodyType)) {
    return 'кузов не подходит';
  }
  if (v.capacityKg < cargo.weightKg) return 'не хватает грузоподъёмности';
  if (v.volumeM3 != null && cargo.volumeM3 != null && v.volumeM3 < cargo.volumeM3) {
    return 'не хватает объёма';
  }
  return null;
}

export default function CargoDetail({ id, onDone, onOpen, onEdit }: Props) {
  const [files, setFiles] = useState<FileRow[]>([]);
  const photosOn = useFeature('cargoPhotos');
  const [cargo, setCargo] = useState<Detail | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [price, setPrice] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fee, setFee] = useState<DealBreakdown | null>(null);
  const [similar, setSimilar] = useState<SimilarCargos | null>(null);
  /*
   * История заявки. Пусто — либо правок не было, либо человек к сделке
   * отношения не имеет: сервер отвечает 404, а не 403, и разбирать эти
   * два случая на клиенте не нужно — в обоих блок просто не показываем.
   */
  const [history, setHistory] = useState<CargoHistoryRow[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  /** Наёмный водитель грузы с биржи не берёт — форму отклика ему не показываем. */
  const [canTake, setCanTake] = useState(true);
  const settings = useSettings();

  /*
   * Последняя цифра, которую подставили мы сами.
   *
   * По ней отличается «поле само заполнилось» от «человек ввёл своё».
   * Пока в поле стоит наша подсказка, чужая ставка её обновляет — иначе
   * человек отправит цену, устаревшую, пока он на неё смотрел. Как только
   * он набрал что-то другое, поле принадлежит ему и не трогается: увести
   * из-под пальцев набираемое число хуже, чем показать устаревшее.
   */
  const suggested = useRef<string>('');

  /*
   * Текущая заявка для обработчиков событий.
   *
   * Обработчик подписан один раз на идентификатор и в замыкании держит
   * заявку такой, какой она была при подписке. Читать её через
   * функциональное обновление состояния нельзя: обновляющая функция
   * обязана быть чистой, а нам нужно из неё же поменять поле ввода.
   */
  const cargoRef = useRef<Detail | null>(null);

  /*
   * Ставка сравнивается с похожими заявками, которые тут же ниже.
   *
   * Медиана по ним — честный ориентир: то же направление, тот же тип
   * машины. Общая «средняя по бирже» смешала бы газели с тралами.
   */
  const marketPerKm = medianOf(
    // Медиана — в рублях: сравнивать тенге с рублями нельзя, а похожие
    // заявки приходят в разных валютах.
    (similar?.items ?? []).map((c) => c.pricePerKmRub ?? 0),
  );
  const tone = rateTone(cargo?.pricePerKmRub ?? null, marketPerKm);

  // Сколько останется на руки при введённой ставке. Считает сервер тем же
  // кодом, что и при заключении сделки, — обещание и факт не разойдутся.
  useEffect(() => {
    const value = Number(price);
    if (!cargo || cargo.isAuthor || !Number.isFinite(value) || value <= 0) {
      setFee(null);
      return;
    }
    const t = setTimeout(() => {
      api.feePreview(id, Math.round(value)).then(setFee).catch(() => setFee(null));
    }, 350);
    return () => clearTimeout(t);
  }, [price, id, cargo]);

  /*
   * Итог торгов, пришедший при открытой карточке. Отдельно от `cargo`,
   * потому что для проигравшего сама карточка уже недоступна, а сказать
   * ему, чем кончилось, всё равно нужно.
   */
  const [auctionOver, setAuctionOver] = useState<
    null | { won: boolean; orderId: string | null }
  >(null);

  useEffect(() => {
    cargoRef.current = cargo;
  }, [cargo]);

  async function refresh() {
    try {
      const c = await api.cargo(id);
      setCargo(c);
      // Поле ставки заполняется само (см. `suggestBid`). Своё значение
      // человека не затираем: `p ||` оставляет набранное.
      setPrice((p) => {
        if (p) return p;
        const s = String(suggestBid(c));
        suggested.current = s;
        return s;
      });

      // Похожие грузим отдельно и молча: блок необязательный, а ждать его
      // ради основной карточки незачем.
      void api.similarCargos(id).then(setSimilar).catch(() => {});
      void api
        .cargoHistory(id)
        .then((r) => setHistory(r.items))
        .catch(() => setHistory([]));

      void api
        .cargoFiles(id)
        .then((r) => setFiles(r.items))
        // Нет доступа к файлам — просто не показываем блок.
        .catch(() => {});

      if (c.isAuthor) {
        const o = await api.offers(id);
        setOffers(o.items);
      } else {
        const me = await api.me();
        setVehicles(me.vehicles);
        setVehicleId((v) => v || (me.vehicles[0]?.id ?? ''));
        setCanTake(me.canTakeCargo);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Новый отклик по этой заявке — подтягиваем список сразу.
  useRealtime(
    'offer:new',
    (e) => {
      if (e.cargoId !== id) return;
      // Отклик на свою заявку — событие, ради которого её и публиковали.
      notify('success');
      void refresh();
    },
    [id],
  );

  // Груз забрали, пока карточка была открыта. Показываем это, а не
  // оставляем кнопку «Беру груз», которая теперь вернёт ошибку.
  useRealtime('cargo:closed', (e) => { if (e.cargoId === id) void refresh(); }, [id]);

  /*
   * Ход торгов — чужая ставка пришла, пока карточка открыта.
   *
   * Перечитывать всю заявку незачем: событие несёт ровно то, что
   * поменялось, — лучшую ставку и срок. А главное, оно двигает поле
   * ввода: наибольшая проходящая ставка теперь другая, и оставить в
   * поле прежнюю значит подготовить человеку отказ.
   */
  useRealtime(
    'auction:public',
    (e) => {
      if (e.cargoId !== id) return;
      syncClock(e.now);
      const now = cargoRef.current;
      if (!now) return;
      const merged = { ...now, bestBid: e.bestBid, bidEndsAt: e.bidEndsAt };
      setCargo(merged);

      const next = String(suggestBid(merged));
      setPrice((p) => {
        if (p !== '' && p !== suggested.current) return p;
        suggested.current = next;
        return next;
      });
    },
    [id],
  );

  /*
   * Торги закрылись при открытой карточке.
   *
   * Окно не закрываем: человек может в этот момент набирать ставку, и
   * выдернуть у него экран хуже, чем показать, что ставки кончились.
   * Меняем только область действия — форма запирается, на её месте исход.
   *
   * Перечитывать карточку тут нельзя: груз стал частной сделкой, и
   * проигравшему сервер справедливо ответит «не найдено». Победителю
   * перечитываем — ему карточка уже доступна как стороне рейса.
   */
  useRealtime(
    'auction:closed',
    (e) => {
      if (e.cargoId !== id) return;
      setAuctionOver({ won: e.won, orderId: e.orderId });
      notify(e.won ? 'success' : 'warning');
      if (e.won) void refresh();
    },
    [id],
  );
  useRealtime('offer:accepted', (e) => { if (e.cargoId === id) void refresh(); }, [id]);
  useRealtime('offer:rejected', (e) => { if (e.cargoId === id) void refresh(); }, [id]);

  /*
   * Чужая ставка в аукционе.
   *
   * Без этого перевозчик торгуется против устаревшей лучшей ставки: форма
   * говорит «пройдёт 97 000», а сервер отвечает отказом, потому что кто-то
   * уже поставил 96 500. В открытом торге видеть текущую цену — не
   * удобство, а само условие игры.
   */
  useRealtime('cargo:new', (e) => { if (e.cargo.id === id) void refresh(); }, [id]);

  async function submitOffer() {
    // Второй рубеж к событию: сокет мог не дойти, вкладка — проспать.
    // Сервер всё равно откажет, но объяснить это лучше здесь.
    if (auctionOver) return;

    const value = Number(price);
    if (!Number.isFinite(value) || value <= 0) {
      return alert('Укажите ставку');
    }

    setBusy(true);
    setError(null);
    try {
      await api.makeOffer(id, {
        price: Math.round(value),
        vehicleId: vehicleId || undefined,
        comment: comment || undefined,
      });
      notify('success');
      alert('Отклик отправлен. Заказчик получит уведомление.');
      await refresh();
    } catch (e) {
      notify('error');
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function accept(offer: Offer) {
    const name =
      offer.driver.company?.name ??
      [offer.driver.firstName, offer.driver.lastName].filter(Boolean).join(' ');

    if (
      !(await confirm(
        `Отдать груз: ${name} за ${formatMoney(offer.price, cargo!.currency)}?`,
      ))
    ) {
      return;
    }

    setBusy(true);
    try {
      await api.acceptOffer(offer.id);
      notify('success');
      alert('Рейс создан. Контакты водителя — во вкладке «Рейсы».');
      onDone();
    } catch (e) {
      notify('error');
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!(await confirm('Снять заявку с публикации?'))) return;
    setBusy(true);
    try {
      await api.cancelCargo(id);
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /*
   * Подтверждение приёмки прямо из карточки.
   *
   * Заказчик приходит сюда по напоминанию «водитель отметил доставку» —
   * и должен закрыть сделку в один тап, не разыскивая рейс в «Рейсах».
   */
  async function confirmDelivery() {
    if (!cargo?.order) return;
    if (!(await confirm('Груз доставлен и принят?'))) return;
    setBusy(true);
    try {
      await api.confirmOrder(cargo.order.id);
      notify('success');
      toast.success('Доставка подтверждена');
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!cargo) return <div className="empty">{error ?? 'Загрузка…'}</div>;

  const cargoAsText = () => cargoToText(cargo);

  // Подходящие машины наверх, остальные под ними с причиной. Порядок внутри
  // групп сохраняется — водитель узнаёт свой парк по привычной череде.
  // Курс заявки, а не сегодняшний: суммы одного блока должны сходиться
  // между собой, а не с курсом на момент открытия экрана.
  const toDeal = makeToDeal(cargo.rateToRub);

  /*
   * Правило продления — площадочная настройка, и в карточке оно должно
   * стоять числом. «Торги могут продлиться» перевозчик прочитает как
   * «когда-нибудь», а ему решать, ставить ли за десять секунд до конца.
   */
  const extendSec = settings?.bidExtendSeconds ?? 0;
  const extendWord =
    extendSec >= 60
      ? `${Math.round(extendSec / 60)} мин`
      : `${extendSec} с`;

  /*
   * Та же проверка, что и на сервере: форма обязана предупредить раньше,
   * чем человек нажмёт. Расхождение здесь означало бы ставку, которую
   * форма приняла, а сервер отклонил.
   */
  const bidError =
    cargo.biddingMode === 'AUCTION' && Number(price) > 0
      ? bidRejection({
          startPrice: cargo.price,
          bestBid: cargo.bestBid,
          bestIsMine: cargo.myOffer != null && cargo.bestBid === cargo.myOffer.price,
          step: cargo.bidStep,
          bid: Math.round(Number(price)),
          endsAt: cargo.bidEndsAt ? new Date(cargo.bidEndsAt) : null,
        })
      : null;

  /*
   * Условия торгов — одинаковые для обеих сторон.
   *
   * Автор их раньше не видел вовсе: блок жил внутри формы отклика, а
   * форму автору не показывают. Получалось, что человек объявил аукцион
   * и не может посмотреть, когда тот кончится и какая ставка лучшая, —
   * при том что решение уходит автоматом именно по этим числам.
   */
  const auctionFacts = cargo.biddingMode === 'AUCTION' && (
    <div className="auction">
      {/* Лучшая ставка — главное число экрана, и набрано оно как
          главное. В ряду одинаковых строк она терялась: человек
          открывает торги ради неё, а читал сверху вниз всё подряд. */}
      <div className="auction__lead">
        <span className="meta">Лучшая ставка</span>
        {cargo.bestBid === null ? (
          <span className="auction__none">ставок ещё нет</span>
        ) : (
          <strong className="auction__best tabular">
            <Money value={cargo.bestBid} currency={cargo.currency} />
          </strong>
        )}
      </div>
      <div className="auction__row">
        <span className="meta">Стартовая цена</span>
        <span className="tabular">
          <Money value={cargo.price} currency={cargo.currency} />
        </span>
      </div>
      {cargo.bidStep > 0 && (
        <div className="auction__row">
          <span className="meta">Шаг понижения</span>
          <span className="tabular">
            <Money value={cargo.bidStep} currency={cargo.currency} />
          </span>
        </div>
      )}
      <div className="auction__row">
        <span className="meta">Торги до</span>
        <span>{cargo.bidEndsAt ? bidDeadline(cargo.bidEndsAt) : 'без срока'}</span>
      </div>
      {/* Правило продления сказано числом, а не «может продлиться»:
          перевозчик планирует по этому времени, и гадать ему не о
          чем. Ноль — продления нет, и об этом тоже прямо. */}
      <div className="meta auction__note">
        {extendSec > 0
          ? `Ставка в последние ${extendWord} продлевает торги на столько же. Дальше груз уходит лучшей ставке.`
          : 'Срок фиксированный: ставки его не продлевают. После указанного времени груз уходит лучшей ставке.'}
      </div>
    </div>
  );

  const ranked = vehicles
    .map((v) => ({ v, why: whyNotFit(v, cargo) }))
    .sort((a, b) => Number(!!a.why) - Number(!!b.why));

  return (
    <>
      {error && <div className="error">{error}</div>}

      {/* Одна карточка вместо трёх: маршрут, деньги, параметры, заказчик и
          отклик. Разнесённые по отдельным карточкам, они заставляли листать
          экран туда-сюда и трижды повторяли одно и то же направление. */}
      <div className="card cargo-sheet">
        {/*
          * На телефоне всё идёт колонкой: маршрут, деньги, факты, заказчик,
          * отклик. На широком экране деньги и отклик уезжают во вторую
          * колонку — до этого там стояла та же телефонная колонка, и до
          * кнопки отклика приходилось листать, хотя половина ширины пустовала.
          *
          * Цена вынесена из шапки соседним блоком, а не спрятана в правую
          * обёртку: обёртка на телефоне встала бы после заказчика, а деньги
          * должны идти сразу за маршрутом.
          */}
        <div className="sheet__what">
            <div className="route">
              <RouteLine
                from={cargo.fromAddress}
                to={cargo.toAddress}
                fromCountry={cargo.fromCountry}
                toCountry={cargo.toCountry}
              />
            </div>
            {/* Номер — в строке с расстоянием, а не в углу карточки.
                Абсолютным позиционированием он налезал на блок цены,
                и «№ 1234» читался поверх суммы. Нужен он ровно в одном
                случае: назвать заявку в разговоре или вписать в документ,
                и место рядом с прочими фактами о рейсе для этого верное. */}
            <div className="meta">
              <span className="tabular">№ {cargo.number}</span>
              {' · '}
              {cargo.distanceKm ? `~${cargo.distanceKm} км` : 'расстояние не рассчитано'}
              {deliveryEta(cargo.distanceKm) && (
                <>
                  {' · в пути ориентировочно '}
                  <strong>{deliveryEta(cargo.distanceKm)!.label}</strong>
                </>
              )}
            </div>

            {/* Быстрые действия: чаще всего заявку из карточки надо кинуть
                текстом в чат («у кого машина на завтра?») — готовим её в
                виде, который не надо переписывать руками. */}
            <div className="action-row" style={{ marginTop: 'var(--s2)' }}>
              <button
                type="button"
                className="secondary btn--compact"
                onClick={() =>
                  void copyText(cargoAsText()).then((ok) =>
                    ok ? toast.success('Заявка скопирована') : toast.error('Не вышло скопировать'),
                  )
                }
              >
                Скопировать заявку
              </button>
            </div>

            {/* Водитель отметил доставку, приёмку ещё не подтвердили —
                заказчик закрывает сделку здесь же. */}
            {cargo.isAuthor &&
              cargo.order?.status === 'DONE' &&
              !cargo.order.confirmedAt && (
                <div className="confirm-cta">
                  <span className="meta">
                    Исполнитель отметил рейс завершённым. Подтвердите приёмку —
                    до этого сделка считается открытой.
                  </span>
                  <button type="button" disabled={busy} onClick={() => void confirmDelivery()}>
                    Подтвердить доставку
                  </button>
                </div>
              )}

            {/* Точный адрес — только сторонам сделки: сервер посторонним
                его не отдаёт. Показываем под маршрутом, потому что это
                уточнение маршрута, а не отдельный факт о грузе. */}
            {(cargo.fromAddressDetail || cargo.toAddressDetail) && (
              <div className="addr-detail">
                {cargo.fromAddressDetail && (
                  <div>
                    <span className="meta">Забор:</span> {cargo.fromAddressDetail}
                  </div>
                )}
                {cargo.toAddressDetail && (
                  <div>
                    <span className="meta">Доставка:</span> {cargo.toAddressDetail}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Цвет ставки — относительно похожих заявок, которые тут же ниже.
              Абсолютных «дорого» и «дёшево» на этом рынке нет: трал и газель
              живут в разных ценовых мирах. */}
        <div className="sheet__money">
            <div className={tone ? `price price--${tone}` : 'price'}>
              {formatMoney(cargo.price, cargo.currency)}
            </div>
            <div className="meta">
              {/* Именно тернарник, а не &&: при ставке ниже 1 ₽/км значение
                  округляется в 0, и React выводит сам этот ноль в разметку. */}
              {/* Всегда в рублях: это мерка против рынка, а рынок рублёвый.
                  В валюте сделки она к тому же вырождается — 604 $ на 900 км
                  дают «1 $/км», то есть ничего. */}
              {cargo.pricePerKmRub ? `${cargo.pricePerKmRub} ₽/км · ` : ''}
              {PAYMENT_TYPE_LABEL[cargo.paymentType]}
              {cargo.delayDays > 0
                ? `, отсрочка ${cargo.delayDays} дн.`
                : ', без отсрочки'}
              {cargo.vatIncluded ? ', с НДС' : ', без НДС'}
            </div>
            {tone && (
              <span className={`rate-tag rate-tag--${tone}`}>
                {RATE_TONE_HINT[tone]}
              </span>
            )}
          </div>

        {/* Дальше — две группы: слева читают, справа действуют. На телефоне
            обёртки просто идут подряд, на широком экране встают колонками. */}
        <div className="sheet__read">
        <div className="divider" />

        {/* Параметры парами «название — значение»: так они читаются как
            таблица, а не как абзац, и номер заявки встаёт в общий ряд. */}
        <dl className="specs">
          <div>
            <dt>Заявка</dt>
            <dd className="tabular">№ {cargo.number}</dd>
          </div>
          {/* Только для закрытых: у обычной заявки строка «видна всем»
              была бы шумом в каждой карточке. */}
          {cargo.audience && (
            <div>
              <dt>Кому видна</dt>
              <dd>
                Только группе «{cargo.audience.name}»
                {cargo.isAuthor && (
                  <div className="meta">В общей ленте заявки нет.</div>
                )}
              </dd>
            </div>
          )}
          <div>
            <dt>Груз</dt>
            <dd>{cargo.title}</dd>
          </div>
          <div>
            <dt>Вес</dt>
            <dd>
              {(cargo.weightKg / 1000).toFixed(1)} т
              {cargo.volumeM3 ? ` · ${cargo.volumeM3} м³` : ''}
            </dd>
          </div>
          <div>
            <dt>Кузов</dt>
            <dd>{cargo.bodyTypes.map((b) => BODY_TYPE_LABEL[b]).join(', ')}</dd>
          </div>
          {/* Дата — второй вопрос после цены: «успею ли я». В общем ряду
              параметров она терялась между весом и кузовом. */}
          <div className="specs--when">
            <dt>Загрузка</dt>
            <dd>
              <span className="when-tag">
                <Icon name="clock" size={13} />
                {new Date(cargo.loadDateFrom).toLocaleDateString('ru-RU')}
                {cargo.loadDateTo &&
                  cargo.loadDateTo !== cargo.loadDateFrom &&
                  ` – ${new Date(cargo.loadDateTo).toLocaleDateString('ru-RU')}`}
              </span>
            </dd>
          </div>
          {cargo.partial && (
            <div>
              <dt>Догруз</dt>
              <dd>
                можно
                {cargo.lengthM ? ` · займёт ${cargo.lengthM} м` : ''}
              </dd>
            </div>
          )}
          {cargo.vehiclesCount > 1 && (
            <div>
              <dt>Машин</dt>
              <dd>{cargo.vehiclesCount}</dd>
            </div>
          )}
          {cargo.loadingTypes.length > 0 && (
            <div>
              <dt>Погрузка</dt>
              <dd>
                {cargo.loadingTypes.map((l) => LOADING_TYPE_LABEL[l]).join(', ')}
                {cargo.roadTrain ? ' · сцепка' : ''}
              </dd>
            </div>
          )}
          {cargo.roadTrain && cargo.loadingTypes.length === 0 && (
            <div>
              <dt>Транспорт</dt>
              <dd>сцепка / автопоезд</dd>
            </div>
          )}
          {cargo.advanceNote && (
            <div>
              <dt>Аванс</dt>
              <dd>{cargo.advanceNote}</dd>
            </div>
          )}
          {cargo.customsNote && (
            <div>
              <dt>Таможня</dt>
              <dd>{cargo.customsNote}</dd>
            </div>
          )}
          {cargo.bodyTypes.includes('MANIPULATOR') &&
            (cargo.craneCapacityKg || cargo.craneReachM) && (
              <div>
                <dt>Кран</dt>
                <dd>
                  {cargo.craneCapacityKg
                    ? `поднять до ${(cargo.craneCapacityKg / 1000).toFixed(1)} т`
                    : ''}
                  {cargo.craneReachM ? ` · вылет от ${cargo.craneReachM} м` : ''}
                </dd>
              </div>
            )}
        </dl>

        {cargo.comment && (
          <>
            <div className="divider" />
            <div className="specs__note">
              <span className="meta">Комментарий заказчика</span>
              <div>{cargo.comment}</div>
            </div>
          </>
        )}

        <div className="divider" />

        {/* Заказчик — строкой в той же карточке, а не отдельной секцией.
            Это часть одной сделки: смотреть на ставку и на того, кто её
            даёт, приходится вместе. */}
        {/* Заказчик — одной строкой. Подробности (сколько заявок, сделок,
            отмен) прячутся в подсказку: до отклика они интересны редко,
            а места занимали половину карточки. */}
        <div className="shipper">
          <span className="shipper__who">
            <strong>{cargo.author.name}</strong>
            <span className="meta">
              {cargo.author.ratingCount > 0
                ? `★ ${cargo.author.rating.toFixed(1)} · ${cargo.author.ratingCount}`
                : 'без оценок'}
            </span>
          </span>

          {cargo.author.isDirectShipper ? (
            <span className="badge badge--direct">прямой</span>
          ) : (
            <span className="badge badge--warn">посредник</span>
          )}
          {cargo.author.verified && (
            <span className="badge badge--verified">проверен</span>
          )}
          <span className="badge">{ENTITY_TYPE_LABEL[cargo.author.entityType]}</span>

          <span
            className="shipper__info"
            tabIndex={0}
            data-tip={
              `Заявок: ${cargo.author.cargosPublished} · ` +
              `сделок: ${cargo.author.ordersDone} · ` +
              `отмен: ${cargo.author.cancelRate !== null ? `${cargo.author.cancelRate}%` : '—'}` +
              (cargo.author.ordersDone === 0
                ? '. Закрытых сделок ещё не было — берите предоплату или работайте по договору'
                : '')
            }
            data-tip-align="end"
            aria-label="Статистика заказчика"
          >
            <Icon name="chart" size={14} />
          </span>

          {/* Предупреждение остаётся на виду: это не справка, а причина
              насторожиться, и прятать её в подсказку нечестно. */}
          {cargo.author.ordersDone === 0 && (
            <span className="badge badge--warn">без закрытых сделок</span>
          )}

          {cargo.contacts && (
            <span className="shipper__contacts">
              {cargo.contacts.phone && (
                <a href={`tel:${cargo.contacts.phone}`}>{cargo.contacts.phone}</a>
              )}
              {cargo.contacts.contactPhone && (
                <a href={`tel:${cargo.contacts.contactPhone}`}>
                  {cargo.contacts.contactPhone} (по заявке)
                </a>
              )}
              {cargo.contacts.tgUsername && (
                <a href={`https://t.me/${cargo.contacts.tgUsername}`}>
                  @{cargo.contacts.tgUsername}
                </a>
              )}
            </span>
          )}
        </div>
        </div>

        <div className="sheet__act">
        {/* Автору условия торгов показываем здесь: на широком экране под
            ценой у него была пустая колонка — форма отклика ему не
            положена, — а сами условия читаются рядом со стартовой ценой,
            от которой и считается понижение. */}
        {cargo.isAuthor && auctionFacts}

        {/* Отклик — здесь же, а не отдельной карточкой ниже.
            Ставку называют, глядя на условия: если развести их по разным
            блокам, приходится листать туда-сюда и держать цифры в голове. */}
        {!cargo.isAuthor && !canTake && (
          <>
            <div className="divider" />
            <div className="empty">
              Рейсы вам назначает диспетчер компании — брать грузы с биржи
              может он. Если это ошибка, попросите открыть вам это право.
            </div>
          </>
        )}

        {/*
          * Торги кончились прямо сейчас — вместо формы исход.
          *
          * Стоит выше формы и заменяет её целиком: оставить рядом живое
          * поле ставки значит предложить действие, которое сервер уже
          * отклонит. Кнопки «закрыть» тут нет намеренно — окно человек
          * закроет сам, когда прочитает.
          */}
        {auctionOver && (
          <>
            <div className="divider" />
            <div className={auctionOver.won ? 'notice notice--ok' : 'notice'}>
              <strong>
                {auctionOver.won ? 'Вы выиграли торги' : 'Торги завершены'}
              </strong>
              <div className="meta notice__note">
                {auctionOver.won
                  ? 'Рейс уже ваш — он появился в разделе «Рейсы».'
                  : 'Груз ушёл участнику с лучшей ставкой. Ставки больше не принимаются.'}
              </div>
            </div>
          </>
        )}

        {!cargo.isAuthor && canTake && !auctionOver && (
          <>
            <div className="divider" />
        <div className="offer">
        <h3>{cargo.myOffer ? 'Ваш отклик' : 'Откликнуться'}</h3>

        {/* Состояние торгов. Перевозчику нужно ровно три вещи: что
            перебивать, каким шагом и до какого часа. */}
        {auctionFacts}

        {cargo.myOffer && (
          <div className="meta offer__mine">
            Отправлено <Money value={cargo.myOffer.price} currency={cargo.currency} /> ·
            статус: {OFFER_STATUS_LABEL[cargo.myOffer.status] ?? cargo.myOffer.status}. Можно
            изменить ставку — отклик обновится.
          </div>
        )}

        {/* Ставка и кнопка в одну строку: это одно действие, и разносить
            их по разным строкам — заставлять тянуться мышью через полкарточки. */}
        <div className="offer__row">
          <div className="field">
            {/* Знак валюты берётся у заявки: отклик уходит в её валюте, и
                «ставка, ₽» над полем, куда вводят тенге, — прямая ложь. */}
            <label>Ваша ставка, {CURRENCY_SIGN[cargo.currency]}</label>
            <input
              type="number"
              inputMode="numeric"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>
          <button
            className="offer__go"
            disabled={busy || cargo.status !== 'ACTIVE' || bidError !== null}
            onClick={() => void submitOffer()}
          >
            {cargo.status !== 'ACTIVE'
              ? 'Неактуальна'
              : cargo.myOffer
                ? 'Обновить'
                : 'Беру груз'}
          </button>
        </div>

        {/* Отказ показываем до нажатия и с цифрой: подбирать проходящую
            ставку вслепую — то, за что торги и не любят. */}
        {bidError && <div className="error offer__error">{bidError}</div>}

        {fee && fee.seller.feeRub !== 0 && (
          <div className="card card--inset payout">
            <div className="row">
              <span className="meta">Наценка площадки</span>
              <span className="meta">
                −<Money value={toDeal(fee.seller.feeRub)} currency={cargo.currency} />
              </span>
            </div>
            <div className="row">
              <strong>На руки</strong>
              <strong className="payout__sum tabular">
                <Money value={toDeal(fee.driverPayoutRub)} currency={cargo.currency} />
              </strong>
            </div>
            {fee.seller.contractName && (
              <div className="meta">
                Контракт: {fee.seller.contractName} · итог {fee.seller.totalPercent}%
              </div>
            )}
          </div>
        )}

        {vehicles.length > 0 && (
          <div className="field">
            <label>Чем повезём</label>
            {/* Машины, которые не подходят, показаны с причиной, а не
                спрятаны: иначе водитель видит короткий список и не понимает,
                куда делся его тягач — и грешит на биржу, а не на кузов. */}
            <div className="truckpick">
              {ranked.map(({ v, why }) => (
                <label
                  key={v.id}
                  className={
                    why
                      ? 'truckpick__row truckpick__row--off'
                      : vehicleId === v.id
                        ? 'truckpick__row truckpick__row--on'
                        : 'truckpick__row'
                  }
                >
                  <input
                    type="radio"
                    name="offer-vehicle"
                    checked={vehicleId === v.id}
                    disabled={!!why}
                    onChange={() => setVehicleId(v.id)}
                  />
                  <span className="truckpick__body">
                    <span className="truckpick__plate">{v.plate}</span>
                    <span className="meta">
                      {BODY_TYPE_LABEL[v.bodyType as keyof typeof BODY_TYPE_LABEL]} ·{' '}
                      {(v.capacityKg / 1000).toFixed(1)} т{why ? ` · ${why}` : ''}
                    </span>
                  </span>
                </label>
              ))}
              <label
                className={
                  vehicleId === '' ? 'truckpick__row truckpick__row--on' : 'truckpick__row'
                }
              >
                <input
                  type="radio"
                  name="offer-vehicle"
                  checked={vehicleId === ''}
                  onChange={() => setVehicleId('')}
                />
                <span className="truckpick__body">
                  <span className="truckpick__plate">Не указывать</span>
                  <span className="meta">Решим машину после сделки</span>
                </span>
              </label>
            </div>
          </div>
        )}

        <div className="field">
          <label>Комментарий</label>
          <input
            placeholder="Например: могу встать под загрузку завтра с утра"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        </div>
          </>
        )}
        </div>
      </div>

      {/* Фото груза и накладные. Прикладывает автор, а после сделки —
          и исполнитель: снимок при погрузке снимает половину споров
          о состоянии груза. */}
      {photosOn && (cargo.isAuthor || cargo.myOffer?.status === 'ACCEPTED' || cargo.contacts) && (
        <div className="card">
          <div className="doc-row">
            <h3 className="doc-row__title">Файлы заявки</h3>
            <UploadButton
              kind="CARGO_PHOTO"
              cargoId={id}
              camera
              label="Файл"
              onDone={(row) => setFiles((prev) => [...prev, row])}
            />
          </div>
          <div className="meta">
            Фото груза, накладные, итоговый документ по перевозке. Видят
            только стороны сделки.
          </div>
          <FileGrid
            items={files}
            onDelete={(fid) => setFiles((prev) => prev.filter((f) => f.id !== fid))}
          />
        </div>
      )}

      {cargo.isAuthor ? (
        <>
          <h3>Отклики ({offers.length})</h3>
          {offers.length === 0 && <div className="empty">Откликов пока нет.</div>}

          {/* Цена вынесена в отдельный столбец и набрана крупно: заказчик
              просматривает отклики ради неё, а раньше она стояла тем же
              кеглем, что и рейтинг с кузовом, и терялась в строке. Все
              отступы внутри карточки заданы сеткой, а не подгонкой по
              месту, — иначе строки не встают друг под друга. */}
          {offers.map((o) => (
            <div
              key={o.id}
              className={
                cargo.biddingMode === 'AUCTION' && cargo.bestBid === o.price
                  ? 'card offercard offercard--best'
                  : 'card offercard'
              }
            >
              <div className="offercard__head">
                <div className="offercard__who">
                  <strong className="offercard__name">
                    {o.driver.company?.name ??
                      [o.driver.firstName, o.driver.lastName].filter(Boolean).join(' ')}
                  </strong>
                  <div className="meta">
                    {o.driver.ratingCount > 0
                      ? `★ ${o.driver.rating.toFixed(1)} · ${o.driver.ratingCount}`
                      : 'без оценок'}
                    {` · рейсов: ${o.driver.ordersDone}`}
                    {o.vehicle &&
                      ` · ${BODY_TYPE_LABEL[o.vehicle.bodyType as keyof typeof BODY_TYPE_LABEL]}, ${(o.vehicle.capacityKg / 1000).toFixed(1)} т`}
                  </div>
                  <div className="offercard__badges">
                    <span className="badge">
                      {ENTITY_TYPE_LABEL[o.driver.entityType]}
                    </span>
                    {o.driver.company?.verified && (
                      <span className="badge badge--verified">проверен</span>
                    )}
                    {o.status === 'ACCEPTED' && (
                      <span className="badge badge--direct">принят</span>
                    )}
                  </div>
                </div>

                <div className="offercard__money">
                  <div className="offercard__price tabular">
                    <Money value={o.price} currency={cargo.currency} />
                  </div>
                  {cargo.biddingMode === 'AUCTION' && cargo.bestBid === o.price && (
                    <span className="offercard__lead">лучшая</span>
                  )}
                </div>
              </div>

              {o.comment && <div className="meta offercard__note">{o.comment}</div>}

              {o.status === 'PENDING' && (
                <button className="offercard__take" disabled={busy} onClick={() => void accept(o)}>
                  Отдать груз
                </button>
              )}
            </div>
          ))}

          {/* Пока заявка висит в ленте, условия ещё можно поправить.
              Как только её взяли, это согласованная сделка, и менять её
              в одностороннем порядке нельзя — только отменять рейс. */}
          {cargo.status === 'ACTIVE' && (
            <div className="action-row">
              <button className="secondary" onClick={() => onEdit(cargo.id)}>
                Изменить условия
              </button>
              <button className="danger" disabled={busy} onClick={() => void cancel()}>
                Снять заявку
              </button>
            </div>
          )}
        </>
      ) : null}

      {/* Похожие заявки: человек уже определился с направлением, и если эта
          не подошла по цене — соседние ближе, чем возврат в ленту с
          перенастройкой фильтров. */}
      {/*
        * История заявки — сторонам сделки, а не только поддержке.
        *
        * Спор «мы договаривались на другую сумму» разбирают сами стороны.
        * Свёрнута по умолчанию: смотрят её редко, а места занимает много.
        */}
      {history.length > 0 && (
        <div className="card">
          <button
            type="button"
            className="secondary"
            onClick={() => setHistoryOpen((v) => !v)}
          >
            {historyOpen ? 'Скрыть историю' : `История заявки (${history.length})`}
          </button>

          {historyOpen && (
            <div className="log">
              {history.map((h) => (
                <div key={h.id} className="log__row">
                  <div className="log__when meta tabular">{historyWhen(h.createdAt)}</div>
                  <div className="log__what">
                    <div className="log__head">
                      <strong>{ACTION_LABEL[h.action] ?? h.action}</strong>
                    </div>
                    <div className="meta">{h.actorName}</div>
                    {h.details && <div className="meta">{h.details}</div>}
                    {h.changes &&
                      Object.entries(h.changes).map(([field, v]) => (
                        <div key={field} className="log__diff">
                          <span className="meta">{field}</span>
                          <span className="log__from">{showValue(v.from)}</span>
                          <span className="meta">→</span>
                          <strong>{showValue(v.to)}</strong>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {similar && similar.items.length > 0 && (
        <>
          <h3 className="sect">{SIMILAR_TITLE[similar.basis]}</h3>
          <div className="similar">
            {similar.items.map((c) => {
              const t = rateTone(c.pricePerKmRub, marketPerKm);
              return (
                <button
                  key={c.id}
                  className="similar__item"
                  onClick={() => onOpen(c.id)}
                >
                  {/* Та же раскладка, что и в основной карточке: слева что
                      везём, справа за сколько. Одинаковые вещи должны
                      выглядеть одинаково — иначе взгляд ищет цену заново
                      в каждом блоке. */}
                  <span className="similar__left">
                    <span className="similar__route">
                      {c.fromRegion} → {c.toRegion}
                    </span>
                    {/* Что везём — из шапки заявки: без этого все строки
                        выглядят одинаково, и выбирать не из чего. */}
                    <span className="similar__what">{c.title}</span>
                    <span className="meta">
                      {(c.weightKg / 1000).toFixed(1)} т
                      {c.distanceKm ? ` · ${c.distanceKm} км` : ''}
                      {c.partial ? ' · догруз' : ''}
                    </span>
                  </span>

                  <span className="similar__right">
                    <span
                      className={t ? `similar__price price--${t}` : 'similar__price'}
                    >
                      {formatMoney(c.price, c.currency)}
                    </span>
                    {c.pricePerKmRub ? (
                      <span className="meta">{c.pricePerKmRub} ₽/км</span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
